// src/views/whatsappInbox/ChatLeftMenuRedux.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "antd";
import { toast } from "react-hot-toast";
import { clearInputValueById } from "../../hooks/use-debounce";
import InboxListRedux from "./InboxListRedux";
import AddChat from "./AddChat";
import FilterDropdown from "./FilterDropdown";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { ArrowUp, Search, X } from "lucide-react";
import "./ChatLeftMenuRedux.css";
import {
  fetchConversations,
  selectAllConversations,
  selectConversationsStatus,
  selectCurrentPage,
  selectFilters,
  selectSelectedConversation,
  selectTotalConversations,
  setCurrentPage,
  setFilter,
  setSelectedConversation,
  startNewChat,
  toggleFilter,
  clearFilters,
  User
} from "../../store/slices/conversationsSlice";
import { setShowAddChatModal, selectShowAddChatModal } from "../../store/slices/uiSlice";
import { Logger } from '../../utils/logger';
import debounce from "lodash/debounce";


interface ChatLeftMenuReduxProps {
    usersArray?: any[]; // For compatibility during migration
    selectedUser?: any;
    onConnect?: (user: any) => void; 
    listInnerRef?: React.RefObject<any>;
    filter?: any;
  }

  const ChatLeftMenuRedux: React.FC<ChatLeftMenuReduxProps> = ({
    usersArray,
    filter,
    selectedUser,
     onConnect, 
    
  }) => {
  // Redux hooks
  const dispatch = useAppDispatch();
  const conversations = useAppSelector(selectAllConversations);
  const selectedConversation = useAppSelector(selectSelectedConversation);
  const filters = useAppSelector(selectFilters);
  const isLoading = useAppSelector(selectConversationsStatus) === 'loading';
  const showAddChatModal = useAppSelector(selectShowAddChatModal);
  const currentPage = useAppSelector(selectCurrentPage);
  const totalConversations = useAppSelector(selectTotalConversations);
  
  const defaultListRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const debouncedFunctionRef = useRef<any>(null);
  const previousFiltersRef = useRef(filters);

  // Local state (for compatibility with existing components)
  const [showAddChat, setShowAddChat] = useState(false);

  // Refs
  const listInnerRef = useRef<any>(null);

  // Logging for debugging
  Logger.log("ChatLeftMenuRedux rendered with conversations:", conversations?.length);

  // Initial fetch is handled by parent WhatsappInbox component
  // No need to duplicate the call here
  

// ✅ UPDATED: Better debounce setup with cleanup
useEffect(() => {
  debouncedFunctionRef.current = debounce((value: string) => {
    Logger.log("Setting search filter (debounced):", { key: 'search', value });
    // ✅ This will trigger the filter useEffect above
    dispatch(setFilter({ key: 'search' as any, value }));
  }, 500); // ✅ Increased debounce time for better performance

  return () => {
    // ✅ Enhanced cleanup
    if (debouncedFunctionRef.current?.cancel) {
      debouncedFunctionRef.current.cancel();
    }
  };
}, [dispatch]);

// ✅ NEW: Cleanup filters when component unmounts
useEffect(() => {
  return () => {
    Logger.log("ChatLeftMenuRedux unmounting, clearing filters");
    dispatch(clearFilters());
  };
}, [dispatch]);

// ✅ UPDATED: Sync local searchValue with Redux filter
useEffect(() => {
  // Keep local searchValue in sync with Redux filter
  setSearchValue(filters.search || '');
}, [filters.search]);

// ✅ UPDATED: Better filter effect that handles both setting and clearing filters
useEffect(() => {
  const previousFilters = previousFiltersRef.current;
  const currentFilters = filters;

  // Check if filters actually changed (not just initial render)
  const filtersChanged = JSON.stringify(previousFilters) !== JSON.stringify(currentFilters);

  if (filtersChanged) {
    Logger.log("Filters changed, fetching conversations with RESET pagination", {
      previous: previousFilters,
      current: currentFilters
    });

    // Always fetch when filters change - whether setting or clearing
    dispatch(fetchConversations({ page: 1, refresh: true }));

    // Update the ref for next comparison
    previousFiltersRef.current = currentFilters;
  }
}, [filters, dispatch]);

// ✅ UPDATED: Improved handleFilter with proper debouncing
const handleFilter = (input: any) => {
  const { name, value } = input.target;
  
  if (name === 'search') {
    // ✅ Update local state immediately for UI responsiveness
    setSearchValue(value);
    
    // ✅ Cancel previous debounced call
    if (debouncedFunctionRef.current?.cancel) {
      debouncedFunctionRef.current.cancel();
    }
    
    // ✅ Trigger debounced search
    debouncedFunctionRef.current(value);
  } else {
    // ✅ Non-search filters trigger immediate action
    dispatch(setFilter({ key: name as any, value }));
  }
};

  const handleTagFilter = (filterKey: string) => {
    dispatch(toggleFilter(filterKey as any));
    Logger.log("Toggling filter:", filterKey);
  };

  const handleRemoveFilter = (filterKey: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent the parent button's onClick from firing
    dispatch(setFilter({ key: filterKey as any, value: undefined })); // Remove the filter
    Logger.log("Removing filter:", filterKey);
  };

  const handleConnect = (user: User) => {
    dispatch(setSelectedConversation(user));
    Logger.log("Setting selected conversation:", user.id);
  };


  
  const handleStartChat = async (data: { phone: string; name?: string }) => {
    try {
      Logger.log("Starting new chat with:", data);
      
      await dispatch(startNewChat({ 
        phone: data.phone,
        message: "Hello!" 
      })).unwrap();
      
      // Refresh conversations after starting a new chat
      dispatch(fetchConversations({ page: 1, refresh: true }));
      
      toast.success("Chat started successfully");
      dispatch(setShowAddChatModal(false));
    } catch (err) {
      console.error("Failed to start chat:", err);
      toast.error("Failed to start chat");
    }
  };

  // Infinite scrolling with back-to-top button visibility
  const onScrollListing = () => {
    if (listInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;

      // Show back-to-top button when scrolled down more than 300px
      setShowBackToTop(scrollTop > 300);

      if (scrollTop + clientHeight >= scrollHeight - 1 && !isLoading) {
        const totalConvo = conversations?.reduce(
          (acc, group) => acc + group.data.length, 0
        ) || 0;

        if (totalConvo < totalConversations) {
          const nextPage = currentPage + 1;
          Logger.log("Loading more conversations, page:", nextPage);
          dispatch(setCurrentPage(nextPage));
          dispatch(fetchConversations({ page: nextPage }));
        }
      }
    }
  };

  // Scroll to top function
  const scrollToTop = () => {
    if (listInnerRef.current) {
      listInnerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="userLists pe-1">
      <div className="chat-list-wrapper">
        {/* New Chat Button */}
        <div style={{
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '4px',
  padding: '0 12px'
}}>
  <span style={{
    fontSize: '20px',
    fontWeight: 600,
    color: '#333',
    fontFamily: 'Gilroy-SemiBold, sans-serif'
  }}>
    Inbox ({totalConversations})
  </span>
  <Button
  onClick={() => dispatch(setShowAddChatModal(true))}
  type="primary"
  style={{
    minWidth: '100px',
    borderRadius: '8px',
    fontWeight: 500,
    height: '32px',
    marginTop: '8px',
    fontFamily: 'Gilroy-Medium, sans-serif'
  }}
>
  New Chat
</Button>

</div>



        <AddChat
          visible={showAddChatModal}
          onClose={() => dispatch(setShowAddChatModal(false))}
          onStartChat={handleStartChat}
        />

        {/* Search & Filter Card */}
        <div className="search-filter-card">
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div className="search-wrapper" style={{ flex: 1 }}>
              <Search className="search-icon" size={16} strokeWidth={2} />
              <input
                ref={inputRef}
                className="search-input"
                placeholder="Type to search..."
                name="search"
                id="searchInput"
                onChange={handleFilter}
                value={searchValue}
                type="text"
              />
              {searchValue && (
                <button
                  className="clear-btn"
                  onClick={() => {
                    // Cancel any pending debounced search
                    if (debouncedFunctionRef.current?.cancel) {
                      debouncedFunctionRef.current.cancel();
                    }

                    // Clear local state immediately for UI feedback
                    setSearchValue("");

                    // Clear Redux state immediately (no debounce for clear)
                    dispatch(setFilter({ key: 'search' as any, value: "" }));

                    // Clear input element (backup)
                    clearInputValueById("searchInput");
                  }}
                >
                  <X size={16} strokeWidth={2.5} />
                </button>
              )}
            </div>

            {/* Filter Dropdown */}
            <FilterDropdown />
          </div>
        </div>

        {/* Chat List */}
        <div className="flex-fill position-relative">
          <div className="userListItems">
            {conversations?.length ? (
              <ul
                className="chat_list_container"
                ref={listInnerRef}
                onScroll={onScrollListing}
              >
            <InboxListRedux
  onConnect={onConnect} // ← NOW USE THE DESTRUCTURED PROP
/>
              </ul>
            ) : (
              <div className="h-100 d-flex align-items-center justify-content-center">
                <h5 className="text-dark fs-6 text-center" style={{
                  fontFamily: 'Gilroy-Medium, sans-serif'
                }}>
                  {isLoading ? "Loading..." : "No results found"}
                </h5>
              </div>
            )}

            {/* Back to Top Button */}
            <button
              className={`back-to-top-btn ${showBackToTop ? 'visible' : ''}`}
              onClick={scrollToTop}
              aria-label="Scroll to top"
            >
              <ArrowUp size={20} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatLeftMenuRedux;